import React, { useMemo } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { makeTheme } from 'styles/appTheme';
import { useBrand } from 'contexts/BrandContext';
import { useColorMode } from 'contexts/ColorModeContext';

/**
 * Paints the dashboard in the selected brand's colour, in light or dark.
 *
 * Switching brand switches the palette; a brand with no colour set gets the
 * platform's default, as do the landing page and the auth screens, which
 * render outside any brand.
 *
 * **On cost**, since this wraps the entire app:
 *
 * The theme is memoised on the colour and the mode, so `createTheme` runs
 * once per distinct pair — on a brand switch or a mode flip, never otherwise. That matters more
 * than it sounds: building the theme inline would return a new object on
 * every render, and since every MUI component subscribes to the theme
 * context, the whole tree would re-render on every unrelated state change.
 * That is the version of this feature that feels slow, and it is the only
 * one worth avoiding.
 *
 * With the memo, a switch costs one object construction and one re-render —
 * against a brand switch that already refetches the brand, its pages, its
 * domains and its counts. The repaint is not the expensive part of that
 * action and will not be what anyone notices.
 *
 * No CSS-variable layer, deliberately. It would avoid the re-render, but MUI
 * v5 components read colours from the theme object rather than from custom
 * properties, so the variables would have to be threaded through every
 * override by hand — a large amount of machinery to save a render that is
 * already hidden behind a network round trip.
 */
export default function BrandThemeProvider({ children }) {
  const { brandList, selectedBrand } = useBrand();
  const { mode } = useColorMode();

  // The list holds wrappers; the brand itself is under `brand_id`.
  const brandColor = useMemo(() => {
    const match = (brandList || [])
      .find((b) => b.brand_id?._id === selectedBrand)?.brand_id;
    return match?.brand_color || match?.company_color || null;
  }, [brandList, selectedBrand]);

  // Keyed on the colour and the mode, not the brand: two brands sharing a
  // colour reuse the same theme object, and re-selecting the same brand is
  // free. Flipping the mode is the one action whose whole point is a repaint,
  // so it is the one case where the re-render is the feature.
  const theme = useMemo(() => makeTheme(brandColor, mode), [brandColor, mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
